import { useMutation } from '@do/seashell/graphql';
import { Checkbox, Text as WalrusText } from '@do/walrus';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { LOGIN_REDIRECT_URL } from '../constants';
import copy from '../copy';
import {
  verifyDevice as VerifyDevice,
  verifyDeviceVariables
} from '../generated-types/verifyDevice';
import { VERIFY_DEVICE } from '../queries/queries';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import { safeGetUri } from '../utils/safe-get-uri';
import {
  validationCopy,
  yupVerificationNumberValidation
} from '../utils/validation-utils';
import ClickLink from './ClickLink';
import DOLink from './DOLink';
import ErrorBanner from './ErrorBanner';
import SpinnerButton from './SpinnerButton';
import Text from './Text';
import TextInput from './TextInput';
import Title from './Title';

const VerificationValidation = Yup.object().shape({
  code: yupVerificationNumberValidation,
  trust_device: Yup.boolean()
});

const VerificationCheckbox = styled(Checkbox)`
  && {
    text-transform: initial;
    letter-spacing: initial;
    font-size: inherit;
  }
`;
interface Props {
  goBack: () => void;
}

const VerificationForm: React.FC<Props> = ({ goBack }) => {
  const [genericServerError, setGenericServerError] = React.useState(null);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [verifyDevice, { loading: isVerifyingDevice }] = useMutation<
    VerifyDevice,
    verifyDeviceVariables
  >(VERIFY_DEVICE);

  return (
    <>
      <Title title={copy.form.verification.title} />
      <Text>{copy.form.verification.details}</Text>

      <Formik
        validateOnBlur={false}
        initialValues={{
          code: '',
          trust_device: false
        }}
        validationSchema={VerificationValidation}
        onSubmit={(values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          verifyDevice({
            variables: {
              verifyDeviceRequest: addSessionData(values)
            }
          })
            .then((successResponse) => {
              const {
                status,
                redirect_url: redirectURL,
                session_data: sessionData
              } = (successResponse.data as VerifyDevice).verifyDevice ?? {};

              saveSessionData(sessionData as string);

              if (status === 'granted') {
                setIsRedirecting(true);
                window.location.assign(
                  safeGetUri(redirectURL as string, LOGIN_REDIRECT_URL)
                );
              } else if (status === 'expired') {
                setGenericServerError(copy.errors.expired_verification_code);
              } else if (status === 'rejected') {
                setGenericServerError(copy.errors.invalid_verification_code);
              }
            })
            .catch(handleFormError(setErrors, setGenericServerError));
        }}
      >
        {({ errors, values, touched, setFieldValue }) => (
          <Form>
            {genericServerError && <ErrorBanner error={genericServerError} />}
            <TextInput
              label={validationCopy.verification.code_label}
              placeholder={`${copy.form.two_factor_auth.placeholder_prefix} ${validationCopy.verification.code_label}`}
              name="code"
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              touched={touched.code}
              error={errors.code}
              value={values.code}
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                setFieldValue('code', e.currentTarget?.value?.trim());
              }}
            />
            <VerificationCheckbox
              className="do-u-mt--small"
              name="trust_device"
              label={copy.form.verification.checkbox_label}
              checked={values.trust_device}
              error={errors.trust_device}
            />
            <SpinnerButton
              className="do-u-mt--small"
              type="submit"
              fullWidth
              spinning={isVerifyingDevice || isRedirecting}
              disabled={isVerifyingDevice || isRedirecting}
              loadingMessage="Verifying your device"
            >
              {copy.form.verification.button_text}
            </SpinnerButton>
          </Form>
        )}
      </Formik>
      <WalrusText type="p" className="need-help-text">
        {copy.form.verification.need_help_text}
      </WalrusText>
      <Text className="do-u-mt--small">
        {copy.form.verification.checkout_text}{' '}
        <DOLink path="/docs/getting-started/faq/#dve">
          {copy.form.verification.troubleshooting_link}
        </DOLink>
        <> {copy.form.or} </>
        <ClickLink onClick={goBack}>
          {copy.form.verification.try_again_link}
        </ClickLink>
      </Text>
    </>
  );
};

export default VerificationForm;
